import moment from 'moment-timezone';
import React, { useMemo, useState } from 'react';
import { TTMenuItem } from '../../../interfaces/menuItem';
import dayjs, { Dayjs } from 'dayjs';
import { bookingApi } from '../../../api/booking';
import { BookingSlots } from '../../../interfaces/booking_schedule';
import { RadioGroup } from '@headlessui/react';
import { message, Spin } from 'antd';
import ThemedDatePicker from '../../components/ThemedDatePicker';
import { LoadingIcon } from '../../components/Spinner';
import { CheckCircleIcon } from '@heroicons/react/20/solid';

type Props = {
  menuItem: TTMenuItem;
  onSelect: (selectedSlot: BookingSlots) => void;
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export function BookingSchdule({ menuItem, onSelect }: Props) {
  const [bookingDate, setBookingDate] = useState<Dayjs>();
  const [bookingSlots, setBookingSlots] = useState<BookingSlots[]>([]);
  const [selectedSlot, setSelectSlot] = useState<BookingSlots | null>(null);
  const [fetchSlots, setFetchingSlots] = useState(false);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const fetchAvailableSlots = async () => {
    try {
      setFetchingSlots(true);
      const response = await bookingApi.getBookingSlots(
        menuItem?.id,
        bookingDate.format('YYYY-MM-DD'),
      );
      setBookingSlots(
        response.data?.filter(
          b =>
            moment(b.date + ' ' + b.start_at, 'YYYY-MM-DD HH:mm:ss') > moment(),
        ),
      );
      setFetchingSlots(false);
    } catch (e) {
      setFetchingSlots(false);
      message.error(
        'Unable to fetch the booking slots. Please try again later.',
      );
    }
  };

  useMemo(() => {
    if (bookingDate) {
      fetchAvailableSlots();
    }
  }, [bookingDate]);

  return (
    <div className="pr-4 pl-4 py-4 bg-surface">
      <div className="bg-primary/10 rounded-md px-2 py-2 border border-outline-variant">
        {/* <h4 className="text-lg font-medium leading-6 text-gray-900">
          Booking Information
        </h4> */}
        <div className="mb-4 flex flex-col items-start">
          <div className="flex flex-row justify-left content-center items-start mb-2">
            <label
              className=" block uppercase tracking-wide text-on-surface text-sm font-bold"
              htmlFor="booking_date_input"
            >
              Booking Date
            </label>
          </div>
          <ThemedDatePicker
            value={bookingDate}
            format="MM/DD/YYYY"
            className="w-full"
            inputReadOnly
            id={'booking_date_input'}
            onChange={date => {
              setBookingDate(date ?? undefined);
            }}
            disabledDate={day => {
              const minDate = dayjs().add(
                menuItem?.booking_settings?.minimum_days_from_booking ?? 0,
                'day',
              );
              const maxDate = dayjs()
                .add(
                  menuItem?.booking_settings?.maximum_days_from_booking ?? 60,
                  'day',
                )
                .endOf('day');
              let is_blackout = false;
              menuItem?.booking_settings?.blackout_dates?.forEach(i => {
                if (
                  moment.utc(i).tz(tz).format('YYYY-MM-DD') ==
                  day.format('YYYY-MM-DD')
                ) {
                  is_blackout = true;
                }
              });
              return (
                day.isBefore(minDate, 'day') ||
                day.isAfter(maxDate, 'day') ||
                is_blackout ||
                menuItem?.booking_settings?.blackout_days?.includes(day.day())
              );
            }}
          />
        </div>
        <div className="relative">
          <div
            className="absolute inset-0 flex items-center"
            aria-hidden="true"
          >
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center">
            {/* <span className="bg-white px-2 text-sm text-gray-500">Continue</span> */}
          </div>
        </div>
        <div className="mt-4">
          <div className="flex flex-row justify-left content-center items-start mb-2">
            <label
              className=" block uppercase tracking-wide text-on-surface text-sm font-bold"
              htmlFor="booking_time_slots"
            >
              Booking Time Slots
            </label>
          </div>
          <Spin
            spinning={fetchSlots}
            tip={'Fetching available slots'}
            indicator={LoadingIcon}
          >
            <RadioGroup
              value={selectedSlot}
              onChange={slot => {
                setSelectSlot(slot);
                onSelect(slot);
              }}
            >
              <RadioGroup.Label className="sr-only">
                {' '}
                Booking Slot{' '}
              </RadioGroup.Label>
              <div className="space-y-4">
                {bookingSlots.map(plan => (
                  <RadioGroup.Option
                    key={plan.start_at}
                    disabled={plan.bookings_remaining <= 0}
                    value={plan}
                    className={({ checked, active }) =>
                      classNames(
                        checked ? 'border-transparent' : 'border-gray-300',
                        active ? 'border-primary ring-2 ring-primary' : '',
                        'relative block cursor-pointer rounded-lg border bg-surface py-4 shadow-sm focus:outline-none sm:flex sm:justify-between',
                      )
                    }
                  >
                    {({ active, checked }) => (
                      <>
                        <span
                          className={`flex items-center  ${
                            checked ? 'px-4' : 'px-6'
                          }`}
                        >
                          {checked && (
                            <CheckCircleIcon className="text-primary w-4 h-4 mr-2" />
                          )}
                          <span className="flex flex-col text-sm  ">
                            <RadioGroup.Label
                              as="span"
                              className="font-medium text-on-surface flex flex-row justify-start"
                            >
                              {moment(plan.start_at, 'HH:mm:ss').format(
                                'hh:mm a',
                              )}{' '}
                              -{' '}
                              {moment(plan.end_at, 'HH:mm:ss').format(
                                'hh:mm a',
                              )}
                            </RadioGroup.Label>
                            <RadioGroup.Description
                              as="span"
                              className="text-on-surface/60"
                            >
                              <span className="block sm:inline">
                                {plan.bookings_remaining !== 0
                                  ? 'spots available'
                                  : 'fully booked'}
                              </span>
                            </RadioGroup.Description>
                          </span>
                        </span>
                        <span
                          className={classNames(
                            active ? 'border' : 'border-2',
                            checked ? 'border-primary' : 'border-transparent',
                            'pointer-events-none absolute -inset-px rounded-lg',
                          )}
                          aria-hidden="true"
                        />
                      </>
                    )}
                  </RadioGroup.Option>
                ))}
              </div>
            </RadioGroup>
          </Spin>
        </div>
      </div>
    </div>
  );
}
